import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebaseConfig';
import { ref, push, set, onValue, remove, serverTimestamp } from 'firebase/database';
import { useAuth } from '../../context/AuthContext';
import { logAdminAction } from '../../firebase/auditLogger';
import { generateNotificationAI } from '../../services/aiService';
import './AdminNotifications.css';

function AdminNotifications() {
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    target: 'all',
    targetUid: '',
    expiresIn: '24', // hours
    reason: '',
    isAiGenerated: false
  });

  useEffect(() => {
    const notifRef = ref(db, 'notifications');
    const unsubscribe = onValue(notifRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const list = Object.keys(data).map(key => ({
          nid: key,
          ...data[key]
        }));
        setNotifications(list.reverse());
      } else {
        setNotifications([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) return alert("Please fill all fields");

    setSaving(true);
    try {
      const notifRef = ref(db, 'notifications');
      const newNotifRef = push(notifRef);
      
      const expiresAt = Date.now() + (parseInt(formData.expiresIn) * 60 * 60 * 1000);

      await set(newNotifRef, {
        notificationId: newNotifRef.key,
        title: formData.title,
        message: formData.message,
        type: formData.type,
        target: formData.target === 'specific' ? formData.targetUid.trim() : formData.target,
        senderAid: userProfile?.adminId || 'SYSTEM',
        senderName: userProfile?.name || 'DevTracker System',
        attribution: formData.isAiGenerated ? 'AI Assisted' : 'Human',
        createdAt: serverTimestamp(),
        expiresAt: expiresAt
      });

      logAdminAction(userProfile?.adminId, `Broadcasted Notification (${formData.target})`, formData.title);
      
      setFormData({
        title: '',
        message: '',
        type: 'info',
        target: 'all',
        targetUid: '',
        expiresIn: '24',
        reason: '',
        isAiGenerated: false
      });
      alert("Broadcast successful!");
    } catch (err) {
      console.error(err);
      alert("Failed to send broadcast.");
    }
    setSaving(false);
  };

  const handleAIGenerate = () => {
    if (!formData.reason) return alert("Please enter a short reason for AI to work with.");
    const { title, message } = generateNotificationAI(formData.type, formData.reason);
    setFormData(prev => ({
      ...prev,
      title,
      message,
      isAiGenerated: true
    }));
  };

  const handleDelete = async (nid) => {
    if (window.confirm("Recall this notification? It will be removed for all targets.")) {
      try {
        await remove(ref(db, `notifications/${nid}`));
        logAdminAction(userProfile?.adminId, "Recalled/Deleted Notification", nid);
      } catch (err) {
        alert("Error deleting notification.");
      }
    }
  };

  return (
    <div className="admin-notifications-page">
      <div className="admin-page-header">
        <div>
          <h1>Broadcast Center</h1>
          <p>Send proactive notifications to specific user tiers.</p>
        </div>
      </div>

      <div className="admin-grid">
        {/* COMPOSE SECTION */}
        <div className="admin-card compose-card">
          <h3><i className="fa-solid fa-paper-plane"></i> New Broadcast</h3>
          <form onSubmit={handleSubmit} className="notif-form">
            <div className="form-group">
              <label>Target Audience</label>
              <select 
                value={formData.target} 
                onChange={(e) => setFormData({...formData, target: e.target.value})}
              >
                <option value="all">Everyone (Public)</option>
                <option value="user">Standard Users Only</option>
                <option value="moderator">Moderators Only</option>
                <option value="support">Support Staff Only</option>
                <option value="specific">Specific User (Input UID)</option>
              </select>
            </div>

            {formData.target === 'specific' && (
              <div className="form-group animate-down">
                <label>Recipient UID</label>
                <input 
                  type="text" 
                  placeholder="Paste user UID here..."
                  value={formData.targetUid}
                  onChange={(e) => setFormData({...formData, targetUid: e.target.value})}
                  required
                />
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label>Alert Type</label>
                <select 
                  value={formData.type} 
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                >
                  <option value="info">Information (Blue)</option>
                  <option value="success">Success (Green)</option>
                  <option value="warning">Warning (Orange)</option>
                  <option value="critical">Critical (Red)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Expiry (Hours)</label>
                <input 
                  type="number" 
                  value={formData.expiresIn} 
                  onChange={(e) => setFormData({...formData, expiresIn: e.target.value})}
                  min="1" max="168"
                />
              </div>
            </div>

            <div className="ai-fast-work-box" style={{
              background: 'rgba(0, 255, 174, 0.05)',
              border: '1px solid rgba(0, 255, 174, 0.15)',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <label style={{color: 'var(--brand-accent)', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px'}}>
                <i className="fa-solid fa-robot"></i> AI ASSISTANT (FAST WORK)
              </label>
              <div style={{display: 'flex', gap: '10px'}}>
                <input 
                  type="text" 
                  placeholder="Enter quick reason (e.g. server maintenance, new update)..."
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  style={{flex: 1, marginBottom: 0}}
                />
                <button 
                  type="button" 
                  onClick={handleAIGenerate}
                  className="btn-admin-ai"
                  style={{
                    padding: '0 15px', 
                    background: 'var(--brand-accent)', 
                    color: '#000', 
                    border: 'none', 
                    borderRadius: '4px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Generate
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Subject</label>
              <input 
                type="text" 
                placeholder="Brief title..." 
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                maxLength="50"
              />
            </div>

            <div className="form-group">
              <label>Message Content</label>
              <textarea 
                placeholder="Describe the update or alert..." 
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                rows="4"
              ></textarea>
            </div>

            <button type="submit" disabled={saving} className="btn-broadcast">
              {saving ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-bullhorn"></i>}
              Broadcast Message
            </button>
          </form>
        </div>

        {/* HISTORY SECTION */}
        <div className="admin-card history-card">
          <h3><i className="fa-solid fa-clock-rotate-left"></i> Sent History</h3>
          <div className="notif-list">
            {loading ? (
              <div className="list-loading">Scanning Network...</div>
            ) : notifications.length === 0 ? (
              <div className="list-empty">No active broadcasts.</div>
            ) : (
              notifications.map(n => (
                <div key={n.nid} className={`notif-history-item status-${n.type}`}>
                  <div className="item-header">
                    <span className="target-badge">{n.target.toUpperCase()}</span>
                    <button onClick={() => handleDelete(n.nid)} className="btn-recall">
                      <i className="fa-solid fa-trash-can"></i>
                    </button>
                  </div>
                  <h4>{n.title}</h4>
                  <p>{n.message}</p>
                  <div className="item-footer">
                    <div className="sender-info">
                      <span>By: <strong>{n.senderName || 'System'}</strong></span>
                      <span className="sender-aid">{n.senderAid || 'N/A'}</span>
                    </div>
                    <div className="attribution-info">
                      {n.attribution === 'AI Assisted' && <span className="ai-badge"><i className="fa-solid fa-robot"></i> AI Assisted</span>}
                      <span>Expires: {new Date(n.expiresAt).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminNotifications;
